"use client";

import { Link } from "react-router-dom";
import {
  Check,
  ChevronRight,
  Crosshair,
  Database,
  ExternalLink,
  LogOut,
  Menu,
  Palette,
  User,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/components/auth/auth-provider";
import { getUserInitials, SYNAPTIC_PROFILE_URL } from "@/lib/auth";
import { cn } from "@/lib/utils";
import type { NavItem, SiteConfig } from "@/types/site-config";

type SiteNavProps = {
  brand: SiteConfig["brand"];
  items: NavItem[];
  developerItems?: NavItem[];
};

export function SiteNav({ brand, items, developerItems = [] }: SiteNavProps) {
  const [open, setOpen] = useState(false);
  const auth = useAuth();
  const showDeveloperMenu = auth.hasRole("developer") && developerItems.length > 0;

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/45 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-3 sm:px-4">
        <Link to="/" className="flex items-center gap-3">
          <Crosshair className="h-5 w-5 text-red-400" aria-hidden />
          <div>
            <p className="text-base font-semibold leading-none">{brand.name}</p>
            <p className="hidden text-xs text-muted-foreground sm:block">{brand.tagline}</p>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
          {items.map((item) => (
            <DesktopFlyoutItem key={item.id} item={item} />
          ))}
          {showDeveloperMenu ? <DeveloperDesktopMenu items={developerItems} /> : null}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <AuthNavItems />
        </div>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="rounded-sm border border-white/15 p-2 lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label="Toggle navigation"
        >
          {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
        </button>
      </div>

      <div
        id="mobile-navigation"
        className={cn(
          "border-t border-white/10 bg-background/97 px-3 py-3 lg:hidden",
          open ? "block" : "hidden",
        )}
      >
        <nav className="grid gap-2" aria-label="Mobile navigation">
          {items.map((item) => (
            <MobileNavItem key={item.id} item={item} onNavigate={() => setOpen(false)} />
          ))}
          {showDeveloperMenu ? (
            <div className="border-t border-white/10 pt-2">
              {developerItems.map((item) => <MobileNavItem key={item.id} item={item} onNavigate={() => setOpen(false)} />)}
            </div>
          ) : null}
          <div className="mt-1 border-t border-white/10 pt-3">
            <AuthNavItems mobile onNavigate={() => setOpen(false)} />
          </div>
        </nav>
      </div>
    </header>
  );
}

function DeveloperDesktopMenu({ items }: { items: NavItem[] }) {
  const menu = useDelayedMenu();
  return (
    <div className="relative" onMouseEnter={menu.openNow} onMouseLeave={menu.closeSoon} onFocus={menu.openNow} onBlur={menu.closeSoon}>
      <button type="button" className="inline-flex items-center gap-2 rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-white/10 hover:text-foreground" aria-expanded={menu.open}>
        <Database className="h-4 w-4" aria-hidden /> Developer
      </button>
      <div className={cn("invisible absolute right-0 top-12 w-80 translate-y-2 rounded-md border border-white/10 bg-popover p-2 opacity-0 shadow-2xl transition", menu.open && "visible translate-y-0 opacity-100")}>
        {items.map((item) => <Link key={item.id} to={item.href} className="block rounded-sm px-3 py-2 hover:bg-white/10"><p className="text-sm font-medium">{item.label}</p><p className="mt-1 text-xs text-muted-foreground">{item.description}</p></Link>)}
      </div>
    </div>
  );
}

function DesktopFlyoutItem({ item }: { item: NavItem }) {
  const menu = useDelayedMenu();

  return (
    <div
      className="relative"
      onMouseEnter={menu.openNow}
      onMouseLeave={menu.closeSoon}
      onFocus={menu.openNow}
      onBlur={menu.closeSoon}
    >
      <Link
        to={item.href}
        className="rounded-sm px-3 py-2 text-sm text-muted-foreground transition hover:bg-white/10 hover:text-foreground"
      >
        {item.label}
      </Link>
      <div
        className={cn(
          "invisible absolute right-0 top-12 w-80 translate-y-2 rounded-md border border-white/10 bg-popover p-2 text-popover-foreground opacity-0 shadow-2xl transition",
          menu.open && "visible translate-y-0 opacity-100",
        )}
      >
        <div className="rounded-sm px-3 py-2">
          <div className="flex items-start justify-between gap-3">
            <p className="font-medium">{item.label}</p>
            <span className="rounded-sm bg-muted px-2 py-1 text-xs text-muted-foreground">{item.status}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
        </div>
        {item.children?.length ? (
          <>
            <div className="my-2 h-px bg-white/10" />
            <div className="grid gap-1">
              {item.children.map((child) => (
                <Link
                  key={child.id}
                  to={child.href}
                  className="rounded-sm px-3 py-2 text-sm transition hover:bg-white/10"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{child.label}</span>
                    <span className="text-xs text-muted-foreground">{child.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{child.description}</p>
                </Link>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function MobileNavItem({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  return (
    <div className="rounded-md border border-white/10 bg-card p-3">
      <Link to={item.href} onClick={onNavigate}>
        <div className="flex items-center justify-between gap-3">
          <span className="font-medium">{item.label}</span>
          <span className="text-xs text-muted-foreground">{item.status}</span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
      </Link>
      {item.children?.length ? (
        <div className="mt-3 grid gap-1 border-t border-white/10 pt-2">
          {item.children.map((child) => (
            <Link
              key={child.id}
              to={child.href}
              onClick={onNavigate}
              className="rounded-sm px-2 py-2 text-sm text-muted-foreground transition hover:bg-white/10 hover:text-foreground"
            >
              {child.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AuthNavItems({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) {
  const auth = useAuth();

  if (auth.isAuthenticated) {
    return <UserProfileMenu mobile={mobile} onNavigate={onNavigate} />;
  }

  return (
    <div className={cn("flex gap-2", mobile ? "grid" : "items-center")}>
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          auth.signIn();
        }}
        className={cn(
          "rounded-sm border border-white/15 px-3 py-2 text-sm text-muted-foreground transition hover:bg-white/10 hover:text-foreground",
          mobile && "w-full text-left",
        )}
      >
        Sign in
      </button>
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          auth.signUp();
        }}
        className={cn(
          "rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/85",
          mobile && "w-full text-left",
        )}
      >
        Sign up
      </button>
    </div>
  );
}

function UserProfileMenu({ mobile, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) {
  const auth = useAuth();
  const menu = useDelayedMenu();
  const initials = getUserInitials(auth.user);

  if (!auth.user) return null;

  if (mobile) {
    return (
      <div className="grid gap-2">
        <UserSummary />
        <a
          href={SYNAPTIC_PROFILE_URL}
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          className="flex items-center justify-between rounded-md border border-white/10 bg-card p-3 text-sm"
        >
          <span className="inline-flex items-center gap-2">
            <User className="h-4 w-4" aria-hidden />
            User profile
          </span>
          <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden />
        </a>
        <SiteThemeMenu mobile />
        <button
          type="button"
          onClick={() => {
            auth.logOut();
            onNavigate?.();
          }}
          className="flex items-center gap-2 rounded-md border border-white/10 bg-card p-3 text-left text-sm text-red-100"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Log out
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={menu.openNow}
      onMouseLeave={menu.closeSoon}
      onFocus={menu.openNow}
      onBlur={menu.closeSoon}
    >
      <button
        type="button"
        className="grid h-10 w-10 place-items-center overflow-hidden rounded-full border border-white/15 bg-muted text-sm font-semibold text-foreground transition hover:border-white/30 hover:bg-white/10"
        aria-label="Open user profile menu"
        aria-expanded={menu.open}
      >
        {auth.user.picture ? (
          <img src={auth.user.picture} alt="" className="h-full w-full object-cover" />
        ) : (
          initials
        )}
      </button>

      <div
        className={cn(
          "invisible absolute right-0 top-12 w-72 translate-y-2 rounded-md border border-white/10 bg-popover p-2 text-popover-foreground opacity-0 shadow-2xl transition",
          menu.open && "visible translate-y-0 opacity-100",
        )}
      >
        <UserSummary />
        <div className="my-2 h-px bg-white/10" />
        <a
          href={SYNAPTIC_PROFILE_URL}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between rounded-sm px-3 py-2 text-sm transition hover:bg-white/10"
        >
          <span className="inline-flex items-center gap-2">
            <User className="h-4 w-4" aria-hidden />
            User profile
          </span>
          <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden />
        </a>
        <SiteThemeMenu />
        <button
          type="button"
          onClick={auth.logOut}
          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-red-100 transition hover:bg-red-500/15"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Log out
        </button>
      </div>
    </div>
  );
}

function UserSummary() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div className="min-w-0 rounded-sm px-3 py-2">
      <p className="truncate text-sm font-medium">{user.name || user.email}</p>
      {user.name ? <p className="truncate text-xs text-muted-foreground">{user.email}</p> : null}
    </div>
  );
}

function SiteThemeMenu({ mobile = false }: { mobile?: boolean }) {
  const menu = useDelayedMenu();
  const [theme, setTheme] = useState("default");
  const options = [
    { id: "default", label: "Default" },
    { id: "signal", label: "Signal" },
    { id: "contrast", label: "High contrast" },
  ];

  useEffect(() => {
    const storedTheme = window.localStorage.getItem("site-theme") || "default";
    setTheme(storedTheme);
    applySiteTheme(storedTheme);
  }, []);

  const chooseTheme = (nextTheme: string) => {
    setTheme(nextTheme);
    window.localStorage.setItem("site-theme", nextTheme);
    applySiteTheme(nextTheme);
  };

  if (mobile) {
    return (
      <div className="rounded-md border border-white/10 bg-card p-3">
        <div className="mb-2 flex items-center gap-2 text-sm">
          <Palette className="h-4 w-4" aria-hidden />
          Site theme options
        </div>
        <div className="grid gap-1">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => chooseTheme(option.id)}
              className="flex items-center justify-between rounded-sm px-2 py-2 text-left text-sm text-muted-foreground transition hover:bg-white/10 hover:text-foreground"
            >
              {option.label}
              {theme === option.id ? <Check className="h-4 w-4 text-accent" aria-hidden /> : null}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={menu.openNow}
      onMouseLeave={menu.closeSoon}
      onFocus={menu.openNow}
      onBlur={menu.closeSoon}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm transition hover:bg-white/10"
        aria-expanded={menu.open}
      >
        <span className="inline-flex items-center gap-2">
          <Palette className="h-4 w-4" aria-hidden />
          Site theme options
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
      </button>
      <div
        className={cn(
          "invisible absolute left-full top-0 ml-2 w-44 translate-x-1 rounded-md border border-white/10 bg-popover p-1 opacity-0 shadow-2xl transition",
          menu.open && "visible translate-x-0 opacity-100",
        )}
      >
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => chooseTheme(option.id)}
            className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm text-muted-foreground transition hover:bg-white/10 hover:text-foreground"
          >
            {option.label}
            {theme === option.id ? <Check className="h-4 w-4 text-accent" aria-hidden /> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

function useDelayedMenu(delay = 100) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  return {
    open,
    openNow: () => {
      clearTimer();
      setOpen(true);
    },
    closeSoon: () => {
      clearTimer();
      timer.current = setTimeout(() => setOpen(false), delay);
    },
  };
}

function applySiteTheme(theme: string) {
  if (theme === "default") {
    delete document.documentElement.dataset.siteTheme;
    return;
  }

  document.documentElement.dataset.siteTheme = theme;
}
